import React, { useState } from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import RestaurantQRCode from './RestaurantQRCode';
import Typography from '@mui/material/Typography';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import { useDispatch } from 'react-redux';
import { showErrorSnackbar, showSuccessSnackbar } from 'store/actions/snackbarMessage';
import QrCodeSettings from './QrCodeSettings';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import API from 'util/API';
import TableQRCodeEditDialog from './TableQRCodeEditDialog';


const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(2)
  },
  paper: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
}));

const fetchTableQrString = async (tableNumber, isPaymentRequired) => {
  const response = await API.get(`/getTableQrString?tableNumber=${tableNumber}&isPaymentRequired=${isPaymentRequired}`);
  return response.data;
};

const createShortUrl = async ({ tableNumber, isPaymentRequired, expiresTimestamp }) => {
  const response = await API.post('/createRestaurantOrderShortUrl', {
    tableNumber,
    isPaymentRequired,
    expiresTimestamp
  });
  return response.data;
};

const TableQRCode = (props) => {
  const { restaurantId, updateQRCodeSetting } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [tableNumber, setTableNumber] = useState('');
  const [searchTableNumber, setSearchTableNumber] = useState('');
  const [paymentRequired, setPaymentRequired] = useState('notRequired');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [neverExpire, setNeverExpire] = useState(false);
  const [editPaymentOption, setEditPaymentOption] = useState('notRequired');
  const [editDisabled, setEditDisabled] = useState(false);

  const showError = (msg) => dispatch(showErrorSnackbar(msg));
  const showSuccess = (msg) => dispatch(showSuccessSnackbar(msg));

  const { data: qrData, isLoading } = useQuery({
    queryKey: ['tableQrString', searchTableNumber, paymentRequired],
    queryFn: () => fetchTableQrString(searchTableNumber, paymentRequired),
    enabled: searchTableNumber !== '',
    staleTime: 5 * 60 * 1000,
  });

  const updateQrCodeMutation = useMutation({
    mutationFn: async ({ tableNumber, paymentRequired, expiryTime, disabled }) => {
      const response = await API.put('/updateQrCodeSettings', {
        tableNumber,
        paymentRequired,
        expiryTime,
        disabled
      });
      return response.data;
    },
    onSuccess: async () => {
      // Invalidate and refetch the table QR string query
      await queryClient.invalidateQueries({ queryKey: ['tableQrString', searchTableNumber, paymentRequired] });
      // Invalidate and refetch all table QR codes
      await queryClient.invalidateQueries({ queryKey: ['allTableQrCodes'] });
      setDialogOpen(false);
      setSelectedDate(null);
      setNeverExpire(false);
      setEditDisabled(false);
      showSuccess('QR code updated successfully!');
    },
    onError: (error) => {
      showError(error.response?.data?.message || 'Failed to update QR code');
    }
  });

  const createShortUrlMutation = useMutation({
    mutationFn: createShortUrl,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tableQrString', searchTableNumber, paymentRequired] });
      queryClient.invalidateQueries({ queryKey: ['allTableQrCodes'] });
      setDialogOpen(false);
      setSelectedDate(null);
    },
    onError: (error) => {
      showError(error.response?.data?.message || 'Failed to create new QR code');
    }
  });

  const item = qrData ? {
    name: searchTableNumber,
    code: qrData.shortUrl || qrData.qrString || qrData.qrCode,
    shortUrl: qrData.shortUrl,
    expiresTimestamp: qrData.expiresTimestamp ? qrData.expiresTimestamp : null,
    disabled: qrData.disabled ?? false
  } : null;

  const handGetTableQrString = () => {
    if (tableNumber.trim() === '') {
      showError('The table number should only include letters and numbers');
      return;
    }
    if (tableNumber.indexOf(' ') > -1 ||
      tableNumber.indexOf('?') > -1 ||
      tableNumber.indexOf('&') > -1 ||
      tableNumber.indexOf('$') > -1 ||
      tableNumber.indexOf(';') > -1) {
      showError('The table number should only include letters and numbers');
      return;
    }
    setSearchTableNumber(tableNumber);
  }

  const handleOpenDialog = () => {
    setEditPaymentOption(paymentRequired);
    setEditDisabled(item?.disabled ?? false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedDate(null);
    setNeverExpire(false);
    setEditDisabled(false);
  };

  const updateExistingQRCode = () => {
    if (!selectedDate && !neverExpire) {
      showError('Please select an expiry date or choose Never Expire');
      return;
    }
    
    // Set expiry time as timestamp (milliseconds) or null for never expire
    const expiryTime = neverExpire ? null : selectedDate.getTime();
    
    updateQrCodeMutation.mutate({
      tableNumber: searchTableNumber,
      paymentRequired: editPaymentOption,
      expiryTime: expiryTime,
      disabled: editDisabled
    });
  };

  const formatExpireTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const updateTableNumber = (value) => {
    setTableNumber(value);
  };

  const handleChange = (event) => {
    setPaymentRequired(event.target.value);
  };

  return (
    <div className={classes.root}>

      <Grid container spacing={3} direction="column">
        <Grid item >
          <Paper className={classes.paper}>
            <Typography variant="h5" >Dine in QR Code</Typography>

            <QrCodeSettings name={'dinein'} updateQRCodeSetting= {updateQRCodeSetting}/>

            <TextField id="outlined-basic"
              value={tableNumber}
              onChange={e => updateTableNumber(e.target.value)}
              label="Table Number" variant="outlined" />

            <FormControl component="fieldset" style={{ marginLeft: theme.spacing(1), marginRight: theme.spacing(1) }}>
              <FormLabel component="legend">Original Online Payment Option</FormLabel>
              <RadioGroup value={paymentRequired} aria-label="payment choice" name="paymentOption" onChange={handleChange}>
                <FormControlLabel value="notRequired" control={<Radio />} label="Not Required" />
                <FormControlLabel value="required" control={<Radio />} label="Required" />
                {/* <FormControlLabel value="optional" control={<Radio />} label="Optional" /> */}
              </RadioGroup>
            </FormControl>


            <Button variant="outlined" color="primary" onClick={handGetTableQrString}>
              OK
            </Button>
          </Paper>
        </Grid>
        <Grid item>
          {isLoading && <Typography>Loading...</Typography>}
          {searchTableNumber !== '' && item && (
            <>
              <RestaurantQRCode
                restaurantId={restaurantId}
                item={item}
                tableNumber={searchTableNumber}
                expiresTimestamp={item.expiresTimestamp}
                formatExpireTime={formatExpireTime}
              />

              <Paper className={classes.paper} style={{ marginTop: theme.spacing(2) }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleOpenDialog}
                >
                  Edit QR Code
                </Button>
              </Paper>
            </>
          )}
        </Grid>
      </Grid>

      {/* Edit QR Code Dialog */}
      <TableQRCodeEditDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        tableNumber={searchTableNumber}
        editPaymentOption={editPaymentOption}
        setEditPaymentOption={setEditPaymentOption}
        editDisabled={editDisabled}
        setEditDisabled={setEditDisabled}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        neverExpire={neverExpire}
        setNeverExpire={setNeverExpire}
        onUpdate={updateExistingQRCode}
        isUpdating={updateQrCodeMutation.isPending}
        formatExpireTime={formatExpireTime}
      />
    </div>
  );
};

export default TableQRCode;
